import {useEffect, useState} from "react";
import {useLocation, useSearchParams} from "react-router-dom";

export type UrlMetadata = Record<string, string>;

interface StoredMetadata {
	readonly data: UrlMetadata;
	readonly timestamp: number;
}

// TTL in milliseconds (1 hour)
const METADATA_TTL = 60 * 60 * 1000;

export const extractUrlMetadata = (searchParams: URLSearchParams): UrlMetadata => {
	return Object.fromEntries(Array.from(searchParams.entries()));
};

/**
 * Creates a localStorage key for storing metadata for a specific survey
 */
const getMetadataStorageKey = (surveyId: string): string => {
	return `survey_metadata_${surveyId}`;
};

/**
 * Checks if two metadata objects are equal
 */
const areMetadataEqual = (metadata1: UrlMetadata, metadata2: UrlMetadata): boolean => {
	const keys1 = Object.keys(metadata1).sort();
	const keys2 = Object.keys(metadata2).sort();

	if (keys1.length !== keys2.length) return false;

	return keys1.every(key => metadata1[key] === metadata2[key]);
};

/**
 * Checks if stored metadata has expired based on TTL
 */
const isMetadataExpired = (timestamp: number): boolean => {
	return Date.now() - timestamp > METADATA_TTL;
};

/**
 * Stores metadata in localStorage for the given survey with timestamp
 */
export const storeUrlMetadata = (surveyId: string, metadata: UrlMetadata): void => {
	try {
		const key = getMetadataStorageKey(surveyId);
		const storedData: StoredMetadata = {
			data: metadata,
			timestamp: Date.now(),
		};
		localStorage.setItem(key, JSON.stringify(storedData));
	} catch (error) {
		console.warn("Failed to store URL metadata:", error);
	}
};

/**
 * Retrieves metadata from localStorage for the given survey if not expired
 */
export const getStoredUrlMetadata = (surveyId: string): UrlMetadata | null => {
	try {
		const key = getMetadataStorageKey(surveyId);
		const stored = localStorage.getItem(key);

		if (!stored) return null;

		const storedData: StoredMetadata = JSON.parse(stored);

		// Check if metadata has expired
		if (isMetadataExpired(storedData.timestamp)) {
			// Remove expired metadata
			localStorage.removeItem(key);
			return null;
		}

		return storedData.data;
	} catch (error) {
		console.warn("Failed to retrieve stored URL metadata:", error);
		return null;
	}
};

/**
 * Clears stored metadata for the given survey
 */
export const clearStoredUrlMetadata = (surveyId: string): void => {
	try {
		const key = getMetadataStorageKey(surveyId);
		localStorage.removeItem(key);
	} catch (error) {
		console.warn("Failed to clear stored URL metadata:", error);
	}
};

/**
 * Gets query parameters from window.location.search (before the hash).
 * This is needed because HashRouter only sees params after the hash.
 */
const getQueryParamsFromWindow = (): URLSearchParams => {
	return new URLSearchParams(window.location.search);
};

/**
 * Enhanced hook that captures URL metadata and persists it for a specific survey.
 * This ensures metadata is preserved even if the user navigates away and returns.
 * Metadata has a 1-hour TTL and fresh metadata always takes precedence.
 *
 * Note: This hook captures query params from both:
 * - window.location.search (params before the hash, e.g., ?participant=123#/path)
 * - React Router's searchParams (params after the hash, e.g., #/path?param=value)
 * Params from window.location.search take precedence in case of duplicates.
 */
export const usePersistentUrlMetadata = (surveyId?: string): UrlMetadata | null => {
	const [metadata, setMetadata] = useState<UrlMetadata | null>(null);
	const [searchParams] = useSearchParams();
	const location = useLocation();

	useEffect(() => {
		if (!surveyId) return;

		// Get params from window.location.search (before the hash)
		const windowSearchParams = getQueryParamsFromWindow();
		// Get params from React Router (after the hash)
		const routerSearchParams = searchParams;

		// Merge both sources, with window.location.search params taking precedence
		const allParams = new URLSearchParams();

		// First add router params (after hash)
		routerSearchParams.forEach((value, key) => {
			allParams.set(key, value);
		});

		// Then add/override with window params (before hash) - these take precedence
		windowSearchParams.forEach((value, key) => {
			allParams.set(key, value);
		});

		// Extract metadata if we have any params
		if (allParams.size > 0) {
			const urlMetadata = extractUrlMetadata(allParams);
			const storedMetadata = getStoredUrlMetadata(surveyId);

			// Check if the new metadata is different from stored metadata
			const isNewMetadata = !storedMetadata || !areMetadataEqual(urlMetadata, storedMetadata);

			if (isNewMetadata) {
				// Clear old metadata and store new metadata
				clearStoredUrlMetadata(surveyId);
				storeUrlMetadata(surveyId, urlMetadata);
			}

			setMetadata(urlMetadata);
		} else {
			// If no URL metadata, try to get stored metadata (if not expired)
			const storedMetadata = getStoredUrlMetadata(surveyId);
			setMetadata(storedMetadata);
		}
	}, [searchParams, location, surveyId]);

	return metadata;
};
